<template>
  <div class="dropdown-action">
    <IdmButton
        class="dropdown-trigger"
        :btnType="'info'"
        :text="''"
        @mouseenter="hover=true"
        @mouseleave="hover=false"
        data-test="scene-info-dropdown-trigger-button"
    />
    <div
        v-if="scene && hover"
        class="dropdown-content"
        @mouseenter="hover=true"
        @mouseleave="hover=false"
    >
      <div class="header">
        <span class="scene-id">Scene ID: {{ scene.id }}</span>
        <IdmButton v-if="copied" :btnType="'small success'" :text="'Copied'" @click.stop="copyId(scene.id)" data-test="scene-info-copied-button">
          <IdmIcon name="v"/>
        </IdmButton>
        <IdmButton v-else class="copy-btn" :btnType="'small secondary'" :text="'Copy ID'" @click.stop="copyId(scene.id)" data-test="scene-info-copy-id-button">
          <div class="icon"></div>
        </IdmButton>
      </div>

      <div class="row">Frame per second (FPS): {{ scene.fps }}</div>
      <div class="row">Resolution: {{ resolution }}</div>
      <div class="row">Aspect ratio: {{ aspectRatio }}</div>
      <div class="row">Original duration: {{ duration }}</div>
      <div class="row">Scene builder version: 3.6</div>

      <div class="row row-descr">
        <span>Scene description</span>
        <IdmTextarea
            :text="scene.description"
            :placeholder="'This is a description'"
            @input="inputDescription"
            data-test="scene-info-description-textarea"
        />
      </div>

      <div class="row row-tags">
        <span>Tags (Separate by ;)</span>
        <IdmInput
            :placeholder="'Intro;Outro'"
            :tags="true"
            :tagsList="scene.tags"
            @inputTags="inputTags"
            data-test="scene-info-tags-input"
        />
      </div>
      <div class="row ai-instructions">
        <div class="ai-instructions-btn" @click="openFileDialog">
          <IdmTooltip text="Import AI instructions" symbol="" onHover/>
        </div>
        <input
            type="file"
            ref="fileInput"
            accept=".json"
            style="display: none"
            @change="handleFileChange"
        />
      </div>
    </div>
  </div>
  <div class="popup-bg ai-instructions-popup" v-if="modal.isShown">
    <IdmPopup
        align="center"
        :title="modal.title"
        :paragraphs="[modal.message]"
    >
      <template v-if="modal.type === 'error' || modal.type === 'success'">
        <IdmButton btnType="secondary" text="Close" @click="closeModal" />
      </template>
      <template v-else-if="modal.type === 'conflict'">
        <IdmButton btnType="secondary" text="Cancel" @click="closeModal" />
        <IdmButton btnType="diverse" text="Replace" @click="handleReplace" />
      </template>
    </IdmPopup>
  </div>
</template>

<script>
import { useBuilderStore } from "@/stores/builderStore";
import sceneCacheStorage from "@/helper/cache";
import _ from 'lodash';

export default {
  name: "sceneInfo",

  props: ['scene', 'duration'],

  data() {
    return {
      hover: false,
      copied: false,
      pendingJson: null,
      modal: {
        isShown: false,
        title: '',
        message: '',
        type: '',
      },
    }
  },

  watch: {
    hover(newVal) {
      if (!newVal) {
        this.copied = false
      }
    }
  },

  computed: {
    resolution() {
      return this.scene.width + 'x' + this.scene.height
    },
    aspectRatio() {
      const gcd = this.gcd(this.scene.width, this.scene.height)
      return this.scene.width / gcd + ":" + this.scene.height / gcd
    },
    builderStore() {
      return useBuilderStore();
    }
  },

  methods: {
    gcd(width, height) {
      return (height === 0) ? width : this.gcd(height, width % height);
    },
    copyId(id) {
      navigator.clipboard.writeText(id).then(
          () => {
            this.copied = true
          },
          (err) => {
            alert("Can not copy", err);
          }
      )
    },
    inputDescription(e) {
      this.builderStore.updateSceneDescription(e.target.value);
      sceneCacheStorage.updateSceneCache(this.scene);
    },
    inputTags(tagsData) {
      const tags = _.reject(tagsData, (tag) => {
        return tag === ''
      });
      this.builderStore.updateSceneTags(tags);
      sceneCacheStorage.updateSceneCache(this.scene);
    },
    openFileDialog() {
      this.$refs.fileInput.click();
    },
    async handleFileChange(event) {
      const file = event.target.files[0];
      if (!file) return;

      if (file.type !== "application/json" && !file.name.endsWith(".json")) {
        this.showModal({
          message: 'Only JSON files are allowed for selection.',
          type: 'error'
        });
        return;
      }

      try {
        const text = await file.text();
        const json = JSON.parse(text);

        const result = await this.builderStore.importSceneData(json);

        if (result) {
          this.showModal({
            message: 'AI instructions were loaded successfully',
            type: 'success'
          });
        } else {
          this.pendingJson = json;
          this.showModal({
            message: `Conflicting changes were found in ${file.name}. How would you like to resolve them?`,
            type: 'conflict'
          });
        }
      } catch (err) {
        console.error(err);
        this.showModal({
          message: 'AI instructions were not loaded',
          type: 'error'
        });
      } finally {
        event.target.value = "";
      }
    },
    showModal({ title = 'Import AI Instructions', message, type = 'info'}) {
      this.modal.isShown = true;
      this.modal.title = title;
      this.modal.message = message;
      this.modal.type = type;
    },
    closeModal() {
      this.modal.isShown = false;
    },
    handleMerge() {
      const result = this.builderStore.mergeData(this.pendingJson);
      this.showModal({
        message: result ? 'AI instructions were loaded successfully' : 'AI instructions were not loaded',
        type: result ? 'success' : 'error'
      });
    },
    handleReplace() {
      const result = this.builderStore.replaceData(this.pendingJson);
      this.showModal({
        message: result ? 'AI instructions were loaded successfully' : 'AI instructions were not loaded',
        type: result ? 'success' : 'error'
      });
    },
  },
}
</script>

<style lang="scss" scoped>
.dropdown-action {
  margin-left: 24px;
  position: relative;
}

.dropdown-content {
  top: calc(100% + 10px);
  width: 376px;
  left: -154px;
  position: absolute;
  background-color: #FAFAFA;
  box-shadow: 0 5px 20px rgba(7, 53, 74, 0.4);
  padding-top: 14px;
  padding-bottom: 12px;
  border-radius: 8px;
  font-size: 16px;
  z-index: 9999999;

  .copy-btn {
    .icon {
      width: 16px;
      height: 16px;
      background-image: url(~@/assets/icons/copy.svg);
      background-repeat: no-repeat;
      background-position: center;
    }
  }
  
  &:after {
    content: "";
    position: absolute;
    top: -10px;
    right: 0;
    width: 100%;
    height: 10px;
  }

  .header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    padding: 0 16px;
    margin-bottom: 8px;

    .scene-id {
      color: #07354A;
      font-weight: 700;
    }
  }

  .row {
    padding: 7px 16px 10px;
    border-bottom: 1px solid #EEEEEE;
    color: #07354A;

    &.row-descr {
      margin-top: 7px;
      padding-top: 0;
      padding-bottom: 0;
      border: none;
    }

    &.row-tags {
      margin-top: 2px;
      padding-bottom: 0;
      border: none;

      .idm-input-container {
        margin-top: 11px;
      }
    }

    .idm-textarea {
      margin-top: 7px;
      padding-top: 2px;
      padding-left: 7px;
      color: #5A7886;
    }

    &.ai-instructions {
      border: none;
      .ai-instructions-btn {
        position: relative;
        top: -30px;
        margin-bottom: -30px;
        width: 32px;
        height: 32px;
        background-color: #CDEDFE;
        background-image: url("data:image/svg+xml;base64,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");
        background-repeat: no-repeat;
        background-position: center center;
        border-radius: 8px;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        &:hover {
          background-color: #e6f6fe;
        }
      }
    }
  }
}
</style>
<style lang="scss">
.ai-instructions {
  .ai-instructions-btn {
    .idm-tooltip {
      .tooltip-icon {
        border: none;
        width: 32px;
        height: 32px;
        border-radius: 8px;
        &.open {
          background-color: unset;
        }
      }
      .text {
        top: 33px;
      }
    }
  }
}
.ai-instructions-popup {
  .btns-container {
    .idm-btn {
      margin-left: unset;
    }
  }
}
</style>